import React, { useState, useEffect } from 'react';
import { User, Mail, Globe, CreditCard, Shield, Download, Trash2, Eye, EyeOff, Moon, Sun, CheckCircle, AlertCircle, Crown, RefreshCw, Settings } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useCountry } from '../CountryContext';
import { useTheme } from '../ThemeContext';
import { getCurrencySymbol } from '../countryConfig';
import SecuritySettings from './SecuritySettings';
import {
  downloadUserData,
  validateEmail,
  validateName,
  getSubscriptionStatusColor,
  formatRenewalDate,
  getAnalyticsPreference,
  setAnalyticsPreference,
  prepareUserDataExport
} from '../utils/profile/profileUtils';
import { getPaymentLinkUrl, getCloudFunctionUrl, PRICING, TRIAL_DAYS } from '../stripeConfig';

const UserProfile = ({
  onUpgrade,
  investments = [],
  cryptocurrencies = [],
  accounts = []
}) => {
  const { currentUser, userProfile, updateUserProfile, logout } = useAuth();
  const { selectedCountry, setSelectedCountry } = useCountry();
  const { theme, toggleTheme, isDark } = useTheme();

  // Personal Info State
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [personalInfo, setPersonalInfo] = useState({
    name: '',
    email: '',
    country: 'US'
  });
  const [infoErrors, setInfoErrors] = useState({});

  // Subscription State
  const [subscriptionData, setSubscriptionData] = useState({
    plan: 'free',
    status: 'active',
    renewalDate: null,
    cancelledAt: null,
    expiresAt: null
  });

  // Privacy State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);

  // Loading states
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Initialize state from userProfile
  useEffect(() => {
    if (userProfile) {
      setPersonalInfo({
        name: userProfile.name || '',
        email: userProfile.email || currentUser?.email || '',
        country: userProfile.country || 'US'
      });

      setSubscriptionData({
        plan: userProfile.plan || 'free',
        status: userProfile.subscriptionStatus || 'active',
        renewalDate: userProfile.renewalDate || null,
        cancelledAt: userProfile.cancelledAt || null,
        expiresAt: userProfile.expiresAt || null
      });
    }

    // Load preferences from localStorage
    setAnalyticsEnabled(getAnalyticsPreference());
  }, [userProfile, currentUser]);

  // Handle Personal Info Save
  const handleSavePersonalInfo = async () => {
    // Validate
    const errors = {};
    if (!validateName(personalInfo.name)) {
      errors.name = 'Please enter a valid name (at least 2 characters)';
    }
    if (!validateEmail(personalInfo.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (Object.keys(errors).length > 0) {
      setInfoErrors(errors);
      return;
    }

    setSaving(true);
    try {
      await updateUserProfile(currentUser.uid, {
        name: personalInfo.name,
        email: personalInfo.email,
        country: personalInfo.country
      });

      // Update country context
      setSelectedCountry(personalInfo.country);

      setIsEditingInfo(false);
      setInfoErrors({});
    } catch (error) {
      console.error('Error saving personal info:', error);
      alert('Failed to save personal information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle Subscription Actions with Stripe Payment Links
  const handleUpgrade = async (billingPeriod = 'monthly') => {
    setSaving(true);
    try {
      const country = userProfile.country || selectedCountry;
      const email = currentUser.email;
      const uid = currentUser.uid;

      // Get Payment Link URL with parameters
      const paymentUrl = getPaymentLinkUrl(country, billingPeriod, email, uid);

      // Redirect to Stripe Payment Link
      window.location.href = paymentUrl;

    } catch (error) {
      console.error('Error initiating upgrade:', error);
      alert('Failed to start upgrade process. Please try again or contact support.');
      setSaving(false);
    }
  };

  // Open Stripe Customer Portal for subscription management
  const handleManageSubscription = async () => {
    setSaving(true);
    try {
      const idToken = await currentUser.getIdToken();

      const response = await fetch(getCloudFunctionUrl('createPortalSession'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          uid: currentUser.uid,
          returnUrl: window.location.href
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (data.isAdmin) {
          alert('You have premium access via admin privileges and do not need to manage a subscription.');
        } else if (data.message) {
          alert(data.message);
        } else {
          alert('Failed to open subscription management. Please try again or contact support.');
        }
        setSaving(false);
        return;
      }

      // Redirect to Stripe Customer Portal
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }

    } catch (error) {
      console.error('Error opening Customer Portal:', error);
      alert('Failed to open subscription management. Please try again or contact support.');
      setSaving(false);
    }
  };

  // Legacy cancel function - now redirects to Customer Portal
  const handleCancelSubscription = async () => {
    await handleManageSubscription();
  };

  // Legacy renew function - now redirects to Customer Portal
  const handleRenewSubscription = async () => {
    await handleManageSubscription();
  };

  // Verify session when returning from Stripe
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      // Show success message
      alert(`Welcome to Premium! Your ${TRIAL_DAYS}-day trial has started. 🎉`);

      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);

      // Verify session and sync if needed (webhook should have already updated Firestore)
      verifyCheckoutSession(sessionId);
    }
  }, []);

  const verifyCheckoutSession = async (sessionId) => {
    try {
      const idToken = await currentUser.getIdToken();

      const response = await fetch(getCloudFunctionUrl('verifySession'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          sessionId: sessionId
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Session verified:', result);

        // Force reload user profile to get latest subscription data
        window.location.reload();
      }
    } catch (error) {
      console.error('Error verifying session:', error);
      // Non-critical error - webhook should have already updated the database
    }
  };

  // Handle Download Personal Data
  const handleDownloadData = () => {
    setDownloading(true);
    try {
      const exportData = prepareUserDataExport(
        userProfile,
        investments,
        cryptocurrencies,
        accounts
      );
      downloadUserData(exportData, personalInfo.email);
      alert('Your data has been downloaded successfully!');
    } catch (error) {
      console.error('Error downloading data:', error);
      alert('Failed to download data. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Handle Delete Account
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      alert('Please type DELETE to confirm account deletion');
      return;
    }

    // Final confirmation
    const finalConfirm = window.confirm(
      '⚠️ FINAL WARNING ⚠️\n\n' +
      'This will PERMANENTLY delete:\n' +
      '• Your account and profile\n' +
      '• All your accounts and investments\n' +
      '• All cryptocurrency data\n' +
      '• All retirement plans and goals\n' +
      '• All support messages\n' +
      '• Your Stripe subscription (if active)\n\n' +
      'This action CANNOT be undone!\n\n' +
      'Are you absolutely sure you want to proceed?'
    );

    if (!finalConfirm) {
      return;
    }

    setSaving(true);

    try {
      console.log('[Delete Account] Starting account deletion process...');

      // Get user ID token for authentication
      const idToken = await currentUser.getIdToken();

      // Call Cloud Function to delete account
      const response = await fetch(getCloudFunctionUrl('deleteUserAccount'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          confirmUid: currentUser.uid,
          deleteReason: 'User requested account deletion via profile settings'
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to delete account');
      }

      console.log('[Delete Account] Account deleted successfully');

      // Clear localStorage
      localStorage.clear();

      // Show success message
      alert(
        '✅ Account Deleted Successfully\n\n' +
        'Your account and all associated data have been permanently deleted.\n\n' +
        'Thank you for using PlanLens. We\'re sorry to see you go!'
      );

      // Log out (this will redirect to landing page)
      await logout();

    } catch (error) {
      console.error('[Delete Account] Error:', error);
      alert(
        '❌ Account Deletion Failed\n\n' +
        error.message + '\n\n' +
        'Please try again or contact support at support@planlens.ai'
      );
    } finally {
      setSaving(false);
      setShowDeleteModal(false);
      setDeleteConfirmation('');
    }
  };

  // Handle Analytics Toggle
  const handleAnalyticsToggle = (enabled) => {
    setAnalyticsEnabled(enabled);
    setAnalyticsPreference(enabled);
    if (enabled) {
      console.log('Analytics tracking enabled');
    } else {
      console.log('Analytics tracking disabled');
    }
  };

  const currencySymbol = getCurrencySymbol(selectedCountry);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
            {personalInfo.name ? personalInfo.name.charAt(0).toUpperCase() : <User className="w-8 h-8" />}
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Profile & Settings</h2>
            <p className="text-gray-600">{personalInfo.email}</p>
          </div>
        </div>
      </div>

      {/* A. Personal Information */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-bold text-gray-900">Personal Information</h3>
          </div>
          {!isEditingInfo ? (
            <button
              onClick={() => setIsEditingInfo(true)}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Edit Info
            </button>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setIsEditingInfo(false);
                  setInfoErrors({});
                  // Reset to original values
                  setPersonalInfo({
                    name: userProfile?.name || '',
                    email: userProfile?.email || currentUser?.email || '',
                    country: userProfile?.country || 'US'
                  });
                }}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePersonalInfo}
                disabled={saving}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            {isEditingInfo ? (
              <div>
                <input
                  type="text"
                  value={personalInfo.name}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, name: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    infoErrors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="John Doe"
                />
                {infoErrors.name && (
                  <p className="text-red-600 text-sm mt-1">{infoErrors.name}</p>
                )}
              </div>
            ) : (
              <p className="text-gray-900">{personalInfo.name || 'Not set'}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
              <Mail className="w-4 h-4" />
              <span>Email Address</span>
            </label>
            {isEditingInfo ? (
              <div>
                <input
                  type="email"
                  value={personalInfo.email}
                  onChange={(e) => setPersonalInfo({ ...personalInfo, email: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    infoErrors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="john@example.com"
                />
                {infoErrors.email && (
                  <p className="text-red-600 text-sm mt-1">{infoErrors.email}</p>
                )}
              </div>
            ) : (
              <p className="text-gray-900">{personalInfo.email}</p>
            )}
          </div>

          {/* Country/Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
              <Globe className="w-4 h-4" />
              <span>Country / Currency</span>
            </label>
            {isEditingInfo ? (
              <select
                value={personalInfo.country}
                onChange={(e) => setPersonalInfo({ ...personalInfo, country: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="US">🇺🇸 United States (USD $)</option>
                <option value="UK">🇬🇧 United Kingdom (GBP £)</option>
              </select>
            ) : (
              <p className="text-gray-900">
                {personalInfo.country === 'US' ? '🇺🇸 United States (USD $)' : '🇬🇧 United Kingdom (GBP £)'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* B. Security Settings */}
      <SecuritySettings />

      {/* C. Subscription Management */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-2 mb-6">
          <CreditCard className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-bold text-gray-900">Subscription Management</h3>
        </div>

        <div className="space-y-6">
          {/* Current Plan */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
            <div>
              <p className="text-sm text-gray-600 mb-1">Current Plan</p>
              <div className="flex items-center space-x-3">
                <p className="text-2xl font-bold text-gray-900 capitalize">{subscriptionData.plan}</p>
                {subscriptionData.plan === 'premium' && (
                  <Crown className="w-6 h-6 text-yellow-500" />
                )}
              </div>
            </div>
            <div className={`px-4 py-2 rounded-full border ${getSubscriptionStatusColor(subscriptionData.status)}`}>
              <p className="text-sm font-semibold capitalize">{subscriptionData.status}</p>
            </div>
          </div>

          {/* Renewal/Expiry Info */}
          {subscriptionData.plan === 'premium' && subscriptionData.renewalDate && (
            <div className="flex items-start space-x-2 p-4 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-900">Active Subscription</p>
                <p className="text-sm text-green-700">
                  {subscriptionData.status === 'cancelled'
                    ? `Access until: ${formatRenewalDate(subscriptionData.expiresAt)}`
                    : `Next renewal: ${formatRenewalDate(subscriptionData.renewalDate)}`
                  }
                </p>
              </div>
            </div>
          )}

          {subscriptionData.status === 'cancelled' && (
            <div className="flex items-start space-x-2 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-yellow-900">Subscription Cancelled</p>
                <p className="text-sm text-yellow-700">
                  You can still use premium features until {formatRenewalDate(subscriptionData.expiresAt)}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            {subscriptionData.plan === 'free' && !userProfile.isAdmin && (
              <button
                onClick={() => handleUpgrade('monthly')}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md disabled:opacity-50"
              >
                <Crown className="w-5 h-5" />
                <span>{saving ? 'Processing...' : 'Upgrade to Premium'}</span>
              </button>
            )}

            {subscriptionData.plan === 'premium' && userProfile.stripeCustomerId && !userProfile.isAdmin && (
              <button
                onClick={handleManageSubscription}
                disabled={saving}
                className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Settings className="w-5 h-5" />
                <span>{saving ? 'Processing...' : 'Manage Subscription'}</span>
              </button>
            )}
          </div>

          {/* Subscription Info */}
          {subscriptionData.plan === 'free' && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Start your {TRIAL_DAYS}-day free trial!</strong> Upgrade to Premium for AI analysis, retirement planning, and advanced features. Cancel anytime.
              </p>
            </div>
          )}

          {subscriptionData.plan === 'premium' && userProfile.stripeCustomerId && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                <strong>Manage your subscription:</strong> Update payment methods, view invoices, or cancel your subscription using the Stripe Customer Portal.
              </p>
            </div>
          )}

          {userProfile.isAdmin && (
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <p className="text-sm text-purple-800">
                <strong>Admin Access:</strong> You have premium features via admin privileges.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* D. Privacy & Data */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-2 mb-6">
          <Shield className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-bold text-gray-900">Privacy & Data</h3>
        </div>

        <div className="space-y-4">
          {/* Download Personal Data */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">Download Your Data</h4>
                <p className="text-sm text-gray-600">
                  Download all your personal information, investments, and settings as a JSON file.
                </p>
              </div>
              <button
                onClick={handleDownloadData}
                disabled={downloading}
                className="ml-4 flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                <span>{downloading ? 'Downloading...' : 'Download'}</span>
              </button>
            </div>
          </div>

          {/* Analytics Tracking */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1">Analytics Tracking</h4>
                <p className="text-sm text-gray-600">
                  Allow anonymous usage data to help improve the app. No personal financial data is shared.
                </p>
              </div>
              <button
                onClick={() => handleAnalyticsToggle(!analyticsEnabled)}
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  analyticsEnabled ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    analyticsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Delete Account */}
          <div className="p-4 border border-red-200 bg-red-50 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-red-900 mb-1">Delete Account</h4>
                <p className="text-sm text-red-700">
                  Permanently delete your account and all associated data. This action cannot be undone.
                </p>
              </div>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="ml-4 flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* F. Theme & Display */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-center space-x-2 mb-6">
          <Settings className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-bold text-gray-900">Theme & Display</h3>
        </div>

        <div className="space-y-4">
          {/* Dark/Light Mode */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 mb-1 flex items-center space-x-2">
                  {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                  <span>{isDark ? 'Dark Mode' : 'Light Mode'}</span>
                </h4>
                <p className="text-sm text-gray-600">
                  Switch between light and dark theme for comfortable viewing.
                </p>
              </div>
              <button
                onClick={toggleTheme}
                className={`ml-4 relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  isDark ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isDark ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center space-x-2 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
              <h3 className="text-xl font-bold text-gray-900">Delete Account</h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                This will permanently delete your account and all associated data, including:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-600 space-y-1 mb-4">
                <li>All investments and portfolio data</li>
                <li>Cryptocurrency holdings</li>
                <li>Account information</li>
                <li>Analysis history</li>
                <li>Saved settings and preferences</li>
              </ul>
              <p className="text-red-600 font-semibold">
                This action cannot be undone!
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type <strong>DELETE</strong> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="DELETE"
              />
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmation('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmation !== 'DELETE'}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Account
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
